import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import Button from "components/basic/button";
import TextField from "components/basic/textField";
import Typography from "components/basic/typography";
import { useResetPasswordMutation } from "generated/graphql";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useSnack } from "hooks/useSnack";
import { ResetValues, validateReset } from "../../validation";

interface ResetStepProps {
  email: string;
  onReset: () => void;
  onResend: () => void;
}

/** Takes the emailed code and the new password. */
const ResetStep: React.FC<ResetStepProps> = ({ email, onReset, onResend }) => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const [, resetPassword] = useResetPasswordMutation();
  const [values, setValues] = useState<ResetValues>({
    code: "",
    password: "",
    confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);
  const { errors, validateAll, revalidate } = useFieldErrors(validateReset);

  const update = (patch: Partial<ResetValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    revalidate(next);
  };

  const handleReset = async () => {
    if (!validateAll(values)) {
      return;
    }
    setSaving(true);
    const response = await resetPassword({
      email: email.trim(),
      code: values.code.trim(),
      password: values.password,
    });
    setSaving(false);

    const error =
      response.data?.resetPassword.errors?.[0]?.message ??
      response.error?.message;
    if (error) {
      showSnack(error, "error");
      return;
    }
    showSnack(t("forgot.updated"), "success");
    onReset();
  };

  return (
    <>
      <Typography variant="body2" align="center">
        {`Enter the 6-digit code we sent to ${email.trim()} and choose a new password.`}
      </Typography>
      <TextField
        size="tiny"
        name="code"
        value={values.code}
        placeHolder={t("forgot.code_placeholder")}
        onChange={({ target: { value } }) => update({ code: value })}
        error={errors.code ?? undefined}
      />
      <TextField
        size="tiny"
        name="password"
        type="password"
        value={values.password}
        placeHolder={t("forgot.new_password")}
        onChange={({ target: { value } }) => update({ password: value })}
        error={errors.password ?? undefined}
      />
      <TextField
        size="tiny"
        name="confirmPassword"
        type="password"
        value={values.confirmPassword}
        placeHolder={t("forgot.confirm_password")}
        onChange={({ target: { value } }) => update({ confirmPassword: value })}
        error={errors.confirmPassword ?? undefined}
      />
      <Button
        onClick={handleReset}
        variant="contained"
        color="warning"
        size="small"
        style={{ color: theme.palette.common.white }}
        loading={saving}
        disabled={saving}
        fullWidth
      >
        {t("forgot.reset")}
      </Button>
      <Button
        onClick={onResend}
        variant="text"
        size="small"
        style={{ color: "orange" }}
      >
        {t("forgot.resend")}
      </Button>
    </>
  );
};

export default ResetStep;
